// src/components/course/interactive/LessonNotes.jsx
import React, { useState, useEffect } from 'react';
import { Save, MessageSquare, Trash2, Plus } from 'lucide-react';

const LessonNotes = ({ lessonId, userId, initialNotes = '', onUpdate }) => {
  const [notes, setNotes] = useState(initialNotes);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setNotes(initialNotes);
  }, [initialNotes]);

  const handleNotesChange = (value) => {
    setNotes(value);
    setHasChanges(true);
  };

  const handleSaveNotes = () => {
    setSaving(true);
    onUpdate(notes);
    setTimeout(() => {
      setSaving(false);
      setHasChanges(false);
    }, 500);
  };

  const handleAddQuestion = () => {
    if (newQuestion.trim()) {
      const question = {
        id: Date.now(),
        text: newQuestion.trim(),
        timestamp: new Date().toISOString(),
        answered: false
      };
      setQuestions([...questions, question]);
      setNewQuestion('');
    }
  };

  const handleDeleteQuestion = (questionId) => {
    setQuestions(questions.filter(q => q.id !== questionId));
  };

  return (
    <div className="space-y-6">
      {/* Notes Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-gray-900">My Notes</label>
          {hasChanges && (
            <button
              onClick={handleSaveNotes}
              disabled={saving}
              className="flex items-center space-x-2 px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save'}</span>
            </button>
          )}
        </div>
        
        <textarea
          value={notes}
          onChange={(e) => handleNotesChange(e.target.value)}
          rows={8}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          placeholder="Take notes about this lesson... These notes are private and only visible to you."
        />
        
        <p className="text-xs text-gray-500 mt-2">
          Your notes are automatically saved and synced across devices.
        </p>
      </div>

      {/* Questions Section */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <MessageSquare className="w-5 h-5 text-gray-600" />
          <label className="text-sm font-medium text-gray-900">Questions</label>
        </div>

        {/* Question List */}
        {questions.length > 0 && (
          <div className="space-y-2 mb-3">
            {questions.map((question) => (
              <div
                key={question.id}
                className="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{question.text}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(question.timestamp).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteQuestion(question.id)}
                  className="ml-2 text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* New Question Input */}
        <div className="flex space-x-2">
          <input
            type="text"
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddQuestion()}
            placeholder="Ask a question about this lesson..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={handleAddQuestion}
            disabled={!newQuestion.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-2">
          Submit questions to your instructor. You'll be notified when they respond.
        </p>
      </div>
    </div>
  );
};

export default LessonNotes;